// Service Dossiers — EPIC A PR2.
// Pattern projet (cf. memory feedback_rls_insert_pattern.md) : INSERT
// passe par RPC SECURITY DEFINER ; SELECT/UPDATE direct via PostgREST.
import { supabase } from '../../lib/supabaseClient';
import type { Tables, Database } from '../../types/database.types';
import type { DossierListItem } from './dossierListDerive';

export type Dossier = Tables<'dossiers'>;
export type DossierMember = Tables<'dossier_members'>;
export type DossierType = Database['public']['Enums']['dossier_type'];
export type DossierStage = Database['public']['Enums']['dossier_stage'];
export type DossierMemberRole = Database['public']['Enums']['dossier_member_role'];

export const DOSSIER_TYPE_LABELS: Record<DossierType, string> = {
  civil: 'Civil',
  penal: 'Pénal',
  commercial: 'Commercial',
  social: 'Social',
  immobilier: 'Immobilier',
  famille: 'Famille',
  admin: 'Administratif',
  autre: 'Autre',
};

export const DOSSIER_STAGE_LABELS: Record<DossierStage, string> = {
  opened: 'Ouvert',
  pre_litigation: 'Pré-contentieux',
  investigation: 'Mise en état',
  first_instance: 'Première instance',
  judgment: 'Jugement rendu',
  appeal: 'Appel',
  enforcement: 'Exécution',
  closed: 'Terminé',
};

export const DOSSIER_STAGE_ORDER: DossierStage[] = [
  'opened',
  'pre_litigation',
  'investigation',
  'first_instance',
  'judgment',
  'appeal',
  'enforcement',
  'closed',
];

export interface DossierFilters {
  search?: string;                       // titre + numéro
  type_affaire?: DossierType[];
  stage?: DossierStage[];
  lead_counsel_id?: string | null;
  include_archived?: boolean;
  include_closed?: boolean;
}

export interface CreateDossierInput {
  title: string;
  type_affaire?: DossierType;
  description?: string | null;
  /** Nom d'une juridiction de la liste du cabinet : la base retrouve l'entrée par son nom. */
  juridiction?: string | null;
  /** Chambre ou formation, texte libre. */
  chambre?: string | null;
  rg_number?: string | null;
  aj_flag?: boolean;
  prescription_date?: string | null;
  next_hearing_at?: string | null;
  client_name?: string | null;          // Provisoire jusqu'à EPIC B (contacts)
  frise_slug?: string | null;
}

export interface UpdateDossierInput {
  title?: string;
  client_procedural_role?: string | null;
  opposing_procedural_role?: string | null;
  description?: string | null;
  type_affaire?: DossierType;
  stage?: DossierStage;
  /** Juridiction typée. Le texte `juridiction` du dossier suit tout seul (trigger). */
  jurisdiction_id?: string | null;
  chambre?: string | null;
  rg_number?: string | null;
  aj_flag?: boolean;
  prescription_date?: string | null;
  next_hearing_at?: string | null;
  lead_counsel_id?: string | null;
  /** Enjeu du dossier en euros. */
  amount_at_stake?: number | null;
}

// ─── SELECT ────────────────────────────────────────────────────────────────

export async function listDossiers(filters: DossierFilters = {}): Promise<Dossier[]> {
  let query = supabase.from('dossiers').select('*');

  // Les dossiers supprimés (soft-delete) sont toujours masqués des listes.
  query = query.is('deleted_at', null);

  if (!filters.include_archived) query = query.is('archived_at', null);
  if (!filters.include_closed)   query = query.is('closed_at', null);

  if (filters.type_affaire?.length) query = query.in('type_affaire', filters.type_affaire);
  if (filters.stage?.length)        query = query.in('stage', filters.stage);
  if (filters.lead_counsel_id)      query = query.eq('lead_counsel_id', filters.lead_counsel_id);

  if (filters.search?.trim()) {
    const s = filters.search.trim();
    query = query.or(`title.ilike.%${s}%,number.ilike.%${s}%`);
  }

  query = query.order('opened_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw new Error(`Failed to list dossiers: ${error.message}`);
  return data ?? [];
}

// ─── LISTE ENRICHIE (registre) ───────────────────────────────────────────────

const ENRICHED_SELECT = `*,
  dossier_contacts(role, is_primary, contact:contacts(id, kind, first_name, last_name, company_name, email)),
  dossier_members(collaborator_id, role),
  tasks(id, content, deadline, due_date, is_completed, is_deleted),
  dossier_activity_log(action, actor_id, details, created_at),
  dossier_frise_steps(position, label)`;

/**
 * Liste enrichie pour la vue registre : embeds parties/équipe/prochaines
 * tâches/dernière activité en une seule requête. La recherche se fait
 * client-side (matchesSearch) — pas de param search ici.
 */
export async function listDossiersEnriched(
  filters: Omit<DossierFilters, 'search'> = {},
): Promise<DossierListItem[]> {
  let query = supabase
    .from('dossiers')
    .select(ENRICHED_SELECT)
    .is('deleted_at', null)
    // Embeds : ne remonter que les tâches ouvertes, et la dernière activité.
    .eq('tasks.is_completed', false)
    .eq('tasks.is_deleted', false)
    .order('deadline', { referencedTable: 'tasks', ascending: true, nullsFirst: false })
    .limit(10, { referencedTable: 'tasks' })
    .order('created_at', { referencedTable: 'dossier_activity_log', ascending: false })
    .limit(1, { referencedTable: 'dossier_activity_log' });

  if (!filters.include_archived) query = query.is('archived_at', null);
  if (!filters.include_closed) query = query.is('closed_at', null);
  if (filters.type_affaire?.length) query = query.in('type_affaire', filters.type_affaire);
  if (filters.stage?.length) query = query.in('stage', filters.stage);
  if (filters.lead_counsel_id) query = query.eq('lead_counsel_id', filters.lead_counsel_id);

  const { data, error } = await query.order('opened_at', { ascending: false });
  if (error) throw new Error(`Failed to list dossiers (enriched): ${error.message}`);
  return (data ?? []) as unknown as DossierListItem[];
}

export async function getDossierById(id: string): Promise<Dossier | null> {
  const { data, error } = await supabase
    .from('dossiers')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(`Failed to fetch dossier: ${error.message}`);
  return data;
}

export async function listDossierMembers(dossierId: string): Promise<DossierMember[]> {
  const { data, error } = await supabase
    .from('dossier_members')
    .select('*')
    .eq('dossier_id', dossierId);
  if (error) throw new Error(`Failed to list dossier members: ${error.message}`);
  return data ?? [];
}

// ─── MUTATIONS ─────────────────────────────────────────────────────────────

export async function createDossier(input: CreateDossierInput): Promise<Dossier> {
  const { data, error } = await supabase.rpc('create_dossier', {
    p_title:             input.title,
    p_type_affaire:      input.type_affaire ?? undefined,
    p_description:       input.description ?? undefined,
    p_juridiction:       input.juridiction ?? undefined,
    p_rg_number:         input.rg_number ?? undefined,
    p_aj_flag:           input.aj_flag ?? undefined,
    p_prescription_date: input.prescription_date ?? undefined,
    p_next_hearing_at:   input.next_hearing_at ?? undefined,
    p_client_name:       input.client_name ?? undefined,
    p_frise_slug:        input.frise_slug ?? undefined,
  } as never);
  if (error) throw new Error(`Failed to create dossier: ${error.message}`);
  if (!data) throw new Error('create_dossier returned no data');
  const dossier = data as Dossier;
  // create_dossier ne connaît pas la chambre : on la pose juste après.
  const chambre = input.chambre?.trim();
  return chambre ? updateDossier(dossier.id, { chambre }) : dossier;
}

export async function updateDossier(id: string, patch: UpdateDossierInput): Promise<Dossier> {
  const { data, error } = await supabase
    .from('dossiers')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(`Failed to update dossier: ${error.message}`);
  return data;
}

export async function archiveDossier(id: string): Promise<Dossier> {
  const { data, error } = await supabase.rpc('archive_dossier', { p_dossier_id: id });
  if (error) throw new Error(`Failed to archive dossier: ${error.message}`);
  return data as Dossier;
}

export async function unarchiveDossier(id: string): Promise<Dossier> {
  const { data, error } = await supabase.rpc('unarchive_dossier', { p_dossier_id: id });
  if (error) throw new Error(`Failed to unarchive dossier: ${error.message}`);
  return data as Dossier;
}

export async function deleteDossier(id: string): Promise<Dossier> {
  const { data, error } = await supabase.rpc('delete_dossier', { p_dossier_id: id });
  if (error) throw new Error(`Failed to delete dossier: ${error.message}`);
  return data as Dossier;
}

export async function closeDossier(id: string, closedAt?: string | null): Promise<Dossier> {
  const { data, error } = await supabase.rpc('close_dossier', {
    p_dossier_id: id,
    p_closed_at: closedAt ?? undefined,
  });
  if (error) throw new Error(`Failed to close dossier: ${error.message}`);
  return data as Dossier;
}

export async function reopenDossier(id: string): Promise<Dossier> {
  const { data, error } = await supabase.rpc('reopen_dossier', { p_dossier_id: id });
  if (error) throw new Error(`Failed to reopen dossier: ${error.message}`);
  return data as Dossier;
}

export async function setLeadCounsel(
  dossierId: string,
  collaboratorId: string,
): Promise<Dossier> {
  const { data, error } = await supabase.rpc('set_lead_counsel', {
    p_dossier_id: dossierId,
    p_collaborator_id: collaboratorId,
  });
  if (error) throw new Error(`Failed to set lead counsel: ${error.message}`);
  if (!data) throw new Error('set_lead_counsel returned no data');
  return data as Dossier;
}

// ─── Stage history (A-04) ────────────────────────────────────────────────

export type DossierStageEntry = Tables<'dossier_stages'>;

export async function listDossierStages(dossierId: string): Promise<DossierStageEntry[]> {
  const { data, error } = await supabase
    .from('dossier_stages')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`Failed to list dossier stages: ${error.message}`);
  return data ?? [];
}

export async function transitionStage(
  dossierId: string,
  toStage: DossierStage,
  note?: string | null,
  effectiveOn?: string | null,
): Promise<Dossier> {
  const { data, error } = await supabase.rpc('transition_stage', {
    p_dossier_id: dossierId,
    p_to_stage: toStage,
    p_note: note ?? undefined,
    p_effective_on: effectiveOn ?? undefined,
  });
  if (error) throw new Error(`Failed to transition stage: ${error.message}`);
  return data as Dossier;
}

export async function setStageEffectiveOn(
  dossierId: string,
  effectiveOn: string,
): Promise<Dossier> {
  const { data, error } = await supabase.rpc('set_stage_effective_on', {
    p_dossier_id: dossierId,
    p_effective_on: effectiveOn,
  });
  if (error) throw new Error(`Failed to set stage date: ${error.message}`);
  return data as Dossier;
}

// ─── Activité (A-09 Aperçu) ───────────────────────────────────────────────

export type DossierActivityEntry = Tables<'dossier_activity_log'>;

export async function listDossierActivity(
  dossierId: string,
  limit = 3,
): Promise<DossierActivityEntry[]> {
  const { data, error } = await supabase
    .from('dossier_activity_log')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`Failed to list dossier activity: ${error.message}`);
  return data ?? [];
}

// ─── DASHBOARD HELPERS ────────────────────────────────────────────────────

/**
 * Compteurs par stage + top 3 dossiers par stage (pour le widget Kanban compact).
 * Filtre archived + closed (sauf le bucket "closed" lui-même).
 */
export interface StageBucket {
  stage: DossierStage;
  count: number;
  top: Dossier[];
}

export async function getStageBuckets(leadCounselId?: string): Promise<StageBucket[]> {
  let query = supabase
    .from('dossiers')
    .select('*')
    .is('deleted_at', null)
    .is('archived_at', null);
  if (leadCounselId) query = query.eq('lead_counsel_id', leadCounselId);
  query = query.order('opened_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch stage buckets: ${error.message}`);

  const byStage = new Map<DossierStage, Dossier[]>();
  for (const d of data ?? []) {
    const arr = byStage.get(d.stage) ?? [];
    arr.push(d);
    byStage.set(d.stage, arr);
  }

  return DOSSIER_STAGE_ORDER.map((stage) => {
    const list = byStage.get(stage) ?? [];
    return { stage, count: list.length, top: list.slice(0, 3) };
  });
}
